import { CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { FC } from 'react';

import AutocompleteLockersInput from '@components/Inputs/AutocompleteLockers/AutocompleteLockers';

import { sxSection } from '../styles';
import { LocationLockersError, LocationState, LocationStateChange } from '../useLocationId';
import { useBarcodeScanner } from 'src/hooks/useBarcodeReader';
import { useGetAllLockersBanksMapQuery } from 'src/redux/api/lockers';
import { toast } from 'sonner';
import isString from 'lodash/isString';

type Props = {
  error: LocationLockersError;
  spot: LocationState;
  onChange: (values: LocationStateChange) => void;
};

const LockerContent: FC<Props> = ({ spot, onChange, error }) => {
    const { data, isLoading } = useGetAllLockersBanksMapQuery();

  useBarcodeScanner((scan) => {
    if (isLoading) return;

    try {
      const parsedJson = JSON.parse(scan);
      if (!isString(parsedJson.manufactureID) || !isString(parsedJson.lockerbankName) || !data) {
        throw new Error('error');
      }

      const item = Object.values(data).find((locker) => locker.externalId === parsedJson.manufactureID);

      if (!item) {
        throw new Error('error');
      }
      
      onChange({
        lockerBankId: item.id,
        lockerCell: parsedJson.manufacturerLockerID,
      });
    } catch (err) {
      toast.error('Unable to identify locker information');
    }
  }, undefined, { withPrint: false, maxInterval: 300 });

  if (isLoading) {
    return <CircularProgress sx={{ alignSelf: 'center' }} />
  }
  return (
    <>
      <Stack sx={sxSection} spacing={3}>
        <Stack direction="row" spacing={0.8}>
          <Typography variant="subtitle1">Storage Group</Typography>
        </Stack>
        <AutocompleteLockersInput
          multiple={false}
          key={spot?.lockerBankId}
          onChange={(ids) => {
            const id = ids[0];
            onChange({
              lockerBankId: id,
              lockerCell: spot.lockerCell,
            });
          }}
          error={error.lockerBankId}
          selectDefaultIfOneOption
          defaultValue={spot?.lockerBankId}
        />
      </Stack>
      <Stack sx={sxSection} spacing={3}>
        <Stack direction="row" spacing={0.8}>
          <Typography variant="subtitle1">Item</Typography>
        </Stack>
        <TextField
          value={spot.lockerCell || ''}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onChange({
              lockerBankId: spot.lockerBankId,
              lockerCell: event.target.value,
            });
          }}
          error={!!error.lockerCell}
          helperText={error.lockerCell}
          fullWidth
          label="Locker Cell"
          required
        />
      </Stack>
    </>
  );
};

export default LockerContent;
